import { Injectable } from "@angular/core";
import { ApiTokenService } from "../api/token/api-token.service";
import { BehaviorSubject, Observable, of } from "rxjs";
import { MicroserviceTokenResponse } from "@sharedV11/interfaces/microservice/microservice.interface";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { IdiomaFilters, ListaIdiomaResult } from "./model";
import { finalize, map, shareReplay, tap } from "rxjs/operators";

const httpOptions = {
	headers: new HttpHeaders({
		"Content-Type": "application/json",
		"Cache-Control": "no-cache",
	}),
};

@Injectable({
	providedIn: "root",
})
export class MicroserviceService {

	private urlApi = "";

  /**
   * Subject com o último token conhecido do microserviço.
   * Isto permite que outras partes da aplicação (interceptors, serviços)
   * consultem rapidamente o estado atual sem acessar o localStorage
   * a cada requisição.
   */
  private tokenSubject: BehaviorSubject<MicroserviceTokenResponse | null>;
  private refreshInProgress$: Observable<string> | null = null;

	constructor(private tokenService: ApiTokenService, private http: HttpClient) {
		this.urlApi = this.tokenService.getConfig("API_MICROSERVICIOS");
    this.tokenSubject = new BehaviorSubject<MicroserviceTokenResponse | null>(
      this.getStoredToken()
    );
	}

	getTokenMicroservice(
		username: string,
		password: string
	): Observable<MicroserviceTokenResponse> {
		const body = {
			username,
			password,
		};

		const URL = `${this.urlApi}/Token`;

		const response = this.http.post<MicroserviceTokenResponse>(URL, body, {
			headers: {
				"Content-Type": "application/json",
				"Cache-Control": "no-cache",
			}
		});

		return response;
	}

	getIdiomasWithMicroservice(filters?: IdiomaFilters): Observable<ListaIdiomaResult> {
		const body: IdiomaFilters = filters ? { ...filters } : {};
		const URL = `${this.urlApi}/api/Maestros/GetIdiomas`;
		
		const response = this.http.get<ListaIdiomaResult>(URL, { params: { ...body }});

		return response;
	}


	refreshToken(data: MicroserviceTokenResponse) {
		const body = {
			token: data.token,
			refreshToken: data.refreshToken,
		};

		const URL = `${this.urlApi}/Refresh`;

		const response = this.http.post<MicroserviceTokenResponse>(URL, body, httpOptions);

		return response;
	}


	loginMicroservice(data: MicroserviceTokenResponse) {
    localStorage.setItem("tokenMicroservice", data.token);
    localStorage.setItem("microserviceData", JSON.stringify(data));

    const expiresAt = this.computeExpirationEpoch(data);
    localStorage.setItem("tokenMicroserviceExpiresAt", expiresAt.toString());

    this.tokenSubject.next(data);
  }

  /**
   * Garante que existe um token válido antes de chamar os microserviços.
   * - Se não existir token → obtém um novo via /Token.
   * - Se o token estiver a menos de 2 minutos de expirar → chama /Refresh.
   * - Caso contrário → reutiliza o token atual.
   */
  ensureValidToken(): Observable<string> {
    const current = this.tokenSubject.value ?? this.getStoredToken();

    // Sem token em memória → pedimos um novo
    if (!current) {
      return this.getTokenMicroservice("", "").pipe(
        tap((data) => this.loginMicroservice(data)),
        map((data) => data.token)
      );
    }

    const expiresAt = this.getExpiresAt(current);
    const now = Date.now();
    const marginMs = 2 * 60 * 1000; // 2 minutos de margem

    if (expiresAt - now > marginMs) {
      // Token ainda é suficientemente válido
      return of(current.token);
    }

    // Token perto de expirar ou já expirado → refrescamos
    if (!this.refreshInProgress$) {
      this.refreshInProgress$ = this.refreshToken(current).pipe(
        tap((data) => this.loginMicroservice(data)),
        map((data) => data.token),
        finalize(() => {
          this.refreshInProgress$ = null;
        }),
        shareReplay(1)
      );
    }

    return this.refreshInProgress$;
  }

  /**
   * Devolve o último token conhecido (se houver) lendo do localStorage.
   */
  private getStoredToken(): MicroserviceTokenResponse | null {
    const raw = localStorage.getItem("microserviceData");
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as MicroserviceTokenResponse;
    } catch {
      return null;
    }
  }

  /**
   * Calcula (ou recalcúla) o instante de expiração em milissegundos.
   * Se o valor não for parseável, assume-se um TTL conservador de 10 minutos.
   */
  private computeExpirationEpoch(data: MicroserviceTokenResponse): number {
    if (data && data.expiracion) {
      const parsed = new Date(data.expiracion).getTime();
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
    // Fallback: 10 minutos a partir de agora
    return Date.now() + 10 * 60 * 1000;
  }

  /**
   * Lê o instante de expiração a partir do storage ou o recalcula
   * com base no token atual.
   */
  private getExpiresAt(token: MicroserviceTokenResponse): number {
    const stored = localStorage.getItem("tokenMicroserviceExpiresAt");
    if (stored && !isNaN(+stored)) {
      return +stored;
    }

    const epoch = this.computeExpirationEpoch(token);
    localStorage.setItem("tokenMicroserviceExpiresAt", epoch.toString());
    return epoch;
  }

	/**
	 * Verifica si el token está próximo a expirar y lo refresca automáticamente
	 * @param minutesBeforeExpiry Minutos antes de la expiración para refrescar (default: 5)
	 * @returns true si se refrescó el token, false si aún es válido
	 */
	checkAndRefreshToken(minutesBeforeExpiry: number = 5): Promise<boolean> {
		return new Promise((resolve, reject) => {
			const microserviceData = localStorage.getItem("microserviceData");
			
			if (!microserviceData) {
				resolve(false);
				return;
			}

			try {
				const parsedData: MicroserviceTokenResponse = JSON.parse(microserviceData);
				
				if (!parsedData.expiracion) {
					resolve(false);
					return;
				}

				// Convertir la fecha de expiración a timestamp
				const expirationDate = new Date(parsedData.expiracion);
				const currentDate = new Date();
				const minutesUntilExpiry = (expirationDate.getTime() - currentDate.getTime()) / (1000 * 60);

				// Si el token expira en menos de X minutos, refrescarlo
				if (minutesUntilExpiry < minutesBeforeExpiry) {
					console.log(`Token expirará en ${minutesUntilExpiry.toFixed(2)} minutos. Refrescando...`);
					
					this.refreshToken(parsedData).subscribe(
						(newToken: MicroserviceTokenResponse) => {
							this.loginMicroservice(newToken);
							console.log('Token refrescado exitosamente');
							resolve(true);
						},
						(error) => {
							console.error('Error al refrescar token:', error);
							reject(error);
						}
					);
				} else {
					// Token aún es válido
					resolve(false);
				}
			} catch (error) {
				console.error('Error al verificar expiración del token:', error);
				reject(error);
			}
		});
	}

	/**
	 * Verifica si el token del microservicio existe y es válido
	 */
	isTokenValid(): boolean {
		const microserviceData = localStorage.getItem("microserviceData");
		
		if (!microserviceData) {
			return false;
		}

		try {
			const parsedData: MicroserviceTokenResponse = JSON.parse(microserviceData);
			
			if (!parsedData.expiracion) {
				return false;
			}

			const expirationDate = new Date(parsedData.expiracion);
			const currentDate = new Date();

			return currentDate < expirationDate;
		} catch (error) {
			return false;
		}
	}
}
